/**
 * Effort Matrix screen (task 13.2, R2, R7.4, R7.7, R7.8).
 *
 * The Effort_Assumption register: effort per unit in minutes and fixed overhead
 * in hours, per Activity_Type, FF role and Population_Unit combination, with the
 * version and effective date each figure belongs to.
 *
 * ## Versions
 *
 * Effort_Assumption submissions never edit a row - every submission is a new
 * version (R2.2), which is what lets a historical calculation reproduce against
 * the version that was effective at the time (R8.3). The table shows the version
 * and effective date, and filters to active versions by default.
 *
 * Capability: `manpower:read` to view, `manpower:write` to maintain (R2.1, R2.5,
 * R7.4, R7.8).
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EffortAssumption } from '@complan/contracts';
import { usePageChrome } from '@/components/layout/page-chrome';
import { hasCapability, useSession } from '@/app/session/session';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  FilterActions,
  FilterBar,
  FilterCheckbox,
  FilterSelect,
} from '@/shared/components/filter-bar';
import { formatDate } from '@/shared/format/date-format';
import { useManpowerReferenceSet, toNameLookup } from './manpower-reference-data';
import {
  useCreateEffortAssumption,
  useEffortAssumptions,
} from './effort-matrix/effort-matrix-data';
import { EffortAssumptionDialog } from './effort-matrix/EffortAssumptionDialog';
import { findingsFromError } from '@/features/admin/admin-findings';
import { FormulaTransparencyBlock } from './shared/FormulaTransparencyBlock';
import { RoleBadge } from './shared/RoleBadge';
import { PosMasterDataSection } from './effort-matrix/PosMasterDataSection';
import { usePopulationOverrides } from './effort-matrix/pos-master-data';
import { FF_ROLE_OPTIONS } from './ff-role-options';

export function EffortMatrixPage(): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const canRead = hasCapability(session.data, 'manpower:read');
  const canWrite = hasCapability(session.data, 'manpower:write');

  usePageChrome({
    title: t('manpower.effortMatrix.title'),
    subtitle: t('manpower.effortMatrix.subtitle'),
  });

  const [activityTypeId, setActivityTypeId] = useState<string | undefined>(undefined);
  const [ffRole, setFfRole] = useState<string | undefined>(undefined);
  const [showAllVersions, setShowAllVersions] = useState<boolean>(false);

  // The editor: `basedOn` seeds a new version of an existing combination (R2.2),
  // or `null` for a blank entry. `openedBy` restores focus to whichever control
  // opened it (R18.6) - a controlled Dialog has no in-tree trigger to return to.
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [basedOn, setBasedOn] = useState<EffortAssumption | null>(null);
  const openedBy = useRef<HTMLElement | null>(null);

  const effortQuery = useEffortAssumptions();
  const createAssumption = useCreateEffortAssumption();

  // A rejected write returns its findings on the thrown error; the dialog renders
  // them and, for R2.5's warning, offers the confirmation that re-submits.
  const findings = findingsFromError(createAssumption.error);

  const openEditor = useCallback(
    (seed: EffortAssumption | null, trigger: HTMLElement | null) => {
      openedBy.current = trigger;
      setBasedOn(seed);
      createAssumption.reset();
      setEditorOpen(true);
    },
    [createAssumption],
  );

  const restoreFocus = useCallback((event: Event) => {
    const trigger = openedBy.current;
    if (trigger !== null) {
      event.preventDefault();
      trigger.focus();
    }
  }, []);
  const enabled = canRead || canWrite;
  const activityTypesQuery = useManpowerReferenceSet('ff-activity-type', { enabled });
  const populationUnitsQuery = useManpowerReferenceSet('population-unit', { enabled });
  const posSegmentsQuery = useManpowerReferenceSet('pos-segment', { enabled });
  const overridesQuery = usePopulationOverrides({ enabled });

  const activityTypes = activityTypesQuery.data ?? [];
  const populationUnits = populationUnitsQuery.data ?? [];
  const activityTypeNames = useMemo(
    () => toNameLookup(activityTypesQuery.data),
    [activityTypesQuery.data],
  );
  const populationUnitNames = useMemo(
    () => toNameLookup(populationUnitsQuery.data),
    [populationUnitsQuery.data],
  );

  // Client-side filtering: the endpoint is unpaged at Manpower's volumetric
  // baseline of at most 50 active combinations (R9.3).
  //
  // "Current" is the highest version of a combination, not `isActive`: a new
  // version does not deactivate its predecessor (`effort-assumption-service.ts`
  // only inserts), so filtering on `isActive` would list the same combination
  // once per version. Deactivation is its own action (R2.8) and gets its own
  // marker.
  const rows = useMemo(() => {
    const items = effortQuery.data?.items ?? [];
    const currentVersionByCombination = new Map<string, number>();
    for (const item of items) {
      const key = combinationKeyOf(item);
      currentVersionByCombination.set(
        key,
        Math.max(currentVersionByCombination.get(key) ?? 0, item.version),
      );
    }

    return items
      .filter(
        (item) =>
          showAllVersions || currentVersionByCombination.get(combinationKeyOf(item)) === item.version,
      )
      .filter((item) => (activityTypeId === undefined ? true : item.activityTypeId === activityTypeId))
      .filter((item) => (ffRole === undefined ? true : item.ffRole === ffRole))
      .map((item) => ({
        assumption: item,
        isCurrent: currentVersionByCombination.get(combinationKeyOf(item)) === item.version,
      }))
      .sort((a, b) => compareAssumptions(a.assumption, b.assumption));
  }, [effortQuery.data, activityTypeId, ffRole, showAllVersions]);

  if (session.isSuccess && !canRead && !canWrite) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.effortMatrix.noAccess')}
        </p>
      </div>
    );
  }

  const columnCount = canWrite ? 8 : 7;

  return (
    <div className="flex flex-col gap-4">
      <FilterBar label={t('manpower.effortMatrix.filterBarLabel')}>
        <FilterSelect
          label={t('manpower.effortMatrix.filterActivityType')}
          value={activityTypeId ?? ''}
          onChange={(value) => setActivityTypeId(value === '' ? undefined : value)}
        >
          <option value="">{t('manpower.effortMatrix.allActivityTypes')}</option>
          {activityTypes.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t('manpower.effortMatrix.filterRole')}
          value={ffRole ?? ''}
          onChange={(value) => setFfRole(value === '' ? undefined : value)}
        >
          <option value="">{t('manpower.effortMatrix.allRoles')}</option>
          {FF_ROLE_OPTIONS.map((role) => (
            <option key={role.id} value={role.id}>
              {role.label}
            </option>
          ))}
        </FilterSelect>

        <FilterCheckbox
          label={t('manpower.effortMatrix.showAllVersions')}
          checked={showAllVersions}
          onChange={setShowAllVersions}
        />

        {canWrite && (
          <FilterActions>
            <Button
              size="sm"
              onClick={(event) => openEditor(null, event.currentTarget)}
              disabled={activityTypes.length === 0 || populationUnits.length === 0}
              {...(activityTypes.length === 0 || populationUnits.length === 0
                ? { title: t('manpower.effortMatrix.needsMasterData') }
                : {})}
            >
              {t('manpower.effortMatrix.add')}
            </Button>
          </FilterActions>
        )}
      </FilterBar>

      {effortQuery.isError && (
        <Alert variant="destructive">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{t('manpower.effortMatrix.loadError')}</AlertDescription>
        </Alert>
      )}

      {!effortQuery.isError && (
        <Card className="gap-2 p-5">
          <p className="text-txt text-[13px] font-semibold">
            {t('manpower.effortMatrix.registerHeading')}
          </p>
          <p className="text-muted-foreground mb-2 text-[11.5px]">
            {t('manpower.effortMatrix.registerSubtitle')}
          </p>
          {effortQuery.isPending ? (
            <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
          ) : (
            <Table>
              <caption className="sr-only">{t('manpower.effortMatrix.tableCaption')}</caption>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col" className="text-[10.5px] uppercase">
                    {t('manpower.effortMatrix.activityType')}
                  </TableHead>
                  <TableHead scope="col" className="text-[10.5px] uppercase">
                    {t('manpower.effortMatrix.role')}
                  </TableHead>
                  <TableHead scope="col" className="text-[10.5px] uppercase">
                    {t('manpower.effortMatrix.populationUnit')}
                  </TableHead>
                  <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                    {t('manpower.effortMatrix.effortPerUnit')}
                  </TableHead>
                  <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                    {t('manpower.effortMatrix.fixedOverhead')}
                  </TableHead>
                  <TableHead scope="col" className="text-[10.5px] uppercase">
                    {t('manpower.effortMatrix.effectiveFrom')}
                  </TableHead>
                  <TableHead scope="col" className="text-[10.5px] uppercase">
                    {t('manpower.effortMatrix.version')}
                  </TableHead>
                  {canWrite && (
                    <TableHead scope="col" className="text-right text-[10.5px] uppercase">
                      <span className="sr-only">{t('manpower.effortMatrix.actions')}</span>
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className="text-muted-foreground py-6 text-center text-[12.3px]"
                    >
                      {t('manpower.effortMatrix.empty')}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map(({ assumption, isCurrent }) => (
                  <TableRow key={assumption.id}>
                    <TableCell className="text-[12.3px]">
                      {activityTypeNames.get(assumption.activityTypeId) ??
                        assumption.activityTypeId}
                      {!isCurrent && (
                        <span className="text-muted-foreground ml-2 text-[11px]">
                          {t('manpower.effortMatrix.superseded')}
                        </span>
                      )}
                      {!assumption.isActive && (
                        <span className="text-muted-foreground ml-2 text-[11px]">
                          {t('manpower.effortMatrix.deactivated')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-[12.3px]">
                      <RoleBadge role={assumption.ffRole} />
                    </TableCell>
                    <TableCell className="text-[12.3px]">
                      {populationUnitNames.get(assumption.populationUnitId) ??
                        assumption.populationUnitId}
                    </TableCell>
                    {/* The unit travels with the figure: a bare `12` beside a bare
                        `20` in the next column reads as two of the same quantity,
                        when one is minutes per unit and the other hours per period. */}
                    <TableCell className="text-right text-[12.3px] tabular-nums">
                      {t('manpower.minutes', { value: assumption.effortPerUnitMinutes })}
                    </TableCell>
                    <TableCell className="text-right text-[12.3px] tabular-nums">
                      {assumption.fixedOverheadHours === null
                        ? t('manpower.absent')
                        : t('manpower.hours', { value: assumption.fixedOverheadHours })}
                    </TableCell>
                    {/* Through the shared formatter, not the raw ISO string the wire
                        carries: every other date on screen goes through it (R18.10).
                        Both this and the version are drawn in the accent colour —
                        they are the provenance of the figures on the row, and a
                        reader checking which assumption produced a number looks for
                        them together. */}
                    <TableCell className="text-accent-line text-[12.3px] tabular-nums">
                      {formatDate(assumption.effectiveFrom)}
                    </TableCell>
                    <TableCell className="text-accent-line text-[12.3px] tabular-nums">
                      {t('manpower.version', { version: assumption.version })}
                    </TableCell>
                    {canWrite && (
                      <TableCell className="text-right">
                        {isCurrent && (
                          <Button
                            type="button"
                            size="xs"
                            variant="outline"
                            onClick={(event) => openEditor(assumption, event.currentTarget)}
                          >
                            {t('manpower.effortMatrix.edit')}
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      <Card className="gap-4 p-5">
        <p className="text-[13px] font-semibold text-txt">
          {t('manpower.effortMatrix.versioningHeading')}
        </p>
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.effortMatrix.versioningBody')}
        </p>
      </Card>

      <PosMasterDataSection
        segments={posSegmentsQuery.data ?? []}
        segmentsPending={posSegmentsQuery.isPending}
        overrides={overridesQuery.data ?? []}
        overridesPending={overridesQuery.isPending}
        overridesError={overridesQuery.isError}
      />

      <FormulaTransparencyBlock />

      {canWrite && (
        <EffortAssumptionDialog
          open={editorOpen}
          onOpenChange={(next) => {
            setEditorOpen(next);
            if (!next) {
              createAssumption.reset();
            }
          }}
          basedOn={basedOn}
          activityTypes={activityTypes}
          populationUnits={populationUnits}
          defaultFfRole={(ffRole as EffortAssumption['ffRole']) ?? 'ade'}
          onSubmit={(payload) => {
            createAssumption.mutate(payload, {
              onSuccess: () => {
                setEditorOpen(false);
                createAssumption.reset();
              },
            });
          }}
          isSubmitting={createAssumption.isPending}
          findings={findings}
          onCloseAutoFocus={restoreFocus}
        />
      )}
    </div>
  );
}

/**
 * The combination a version belongs to, matching what
 * `effort-assumption-service.ts` groups versions by.
 */
function combinationKeyOf(assumption: EffortAssumption): string {
  return [assumption.activityTypeId, assumption.ffRole, assumption.populationUnitId].join('|');
}

/** Activity_Type, then role, then Population_Unit, then newest version first. */
function compareAssumptions(a: EffortAssumption, b: EffortAssumption): number {
  return (
    a.activityTypeId.localeCompare(b.activityTypeId) ||
    a.ffRole.localeCompare(b.ffRole) ||
    a.populationUnitId.localeCompare(b.populationUnitId) ||
    b.version - a.version
  );
}


